import type { DateTime } from "luxon";
import { useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import type { AutoPromptEffort, AutoPromptReport } from "../../../services/backend.service";
import {
	type FailedTab,
	useCreatePromptHistory,
	useLoadNewNurses,
	useLoadScheduleRecurringShifts,
	useLoadScheduleShifts,
	useRunAutoPrompt,
	useTransformNursesCsv,
	useTransformShiftCsv,
	useUpdateDepartmentFirstSunday,
} from "../hooks";
import { earliestDateInCsv, type LoadOrderEntry, orderFilesChronologically, SHIFTS_DATE_COLUMN } from "../loadOrder";
import { stripExtension } from "../utils";
import type { LoadRecurringResult, LoadShiftsResult } from "./LoadResultDisplay";
import type { LoadNursesResults } from "./NursesSummary";
import type { PreviewFileType } from "./PreviewCard";

// "roster" maps to backend's nurses file, "shifts" to actual schedule, "template" to recurring template.
export type AutoPromptFileKind = "roster" | "shifts" | "template";

export type AutoPromptFile = {
	id: string;
	file: File;
	kind: AutoPromptFileKind;
};

export type TransformOutcome = {
	csvBuffer: ArrayBuffer;
	// Earliest date in the payload that will actually be uploaded (shifts only). Stored rather than
	// derived during render: re-scanning every file's CSV on each render is expensive, and the preview
	// filter can drop a file's earliest rows, which changes where that file belongs in the order.
	earliestDate?: DateTime | null;
	// Filtered/sorted blob from the PreviewCard. Falls back to csvBuffer if the user never opened the preview.
	filteredBlob?: Blob;
	failedTabs: FailedTab[];
	skip: boolean;
	uploaded: boolean;
	// Id of the saved onboarding_prompt_history row, linked to the load call so the row is marked loaded.
	promptHistoryId?: string;
	loadResult?: LoadShiftsResult | LoadRecurringResult | LoadNursesResults | null;
};

export type Phase = "upload" | "analysis" | "prompt" | "transform";

// Transform results are keyed by file id, not by kind: a run can carry several roster or shifts files
// (DAT-652), and keying by kind collapsed them onto one entry.
export type TransformOutcomes = Record<string, TransformOutcome>;

export type AutoPromptEditable = {
	schedule_symbols: string;
	locations: string;
	master_schedule_orientation: string;
	shift_times: string;
	name_matching: string;
	file_dates: string;
};

export const KIND_LABEL: Record<AutoPromptFileKind, string> = {
	roster: "Roster",
	shifts: "Shifts",
	template: "Template",
};

// Translation keys, not labels — the switch resolves them through t() at render. Keyed by the union so
// a level added to AutoPromptEffort is a compile error here rather than a level that silently can't be
// picked, the same guarantee KIND_LABEL gives AutoPromptFileKind above. The enum is hand-mirrored from
// the backend (no codegen in this repo) and ExternalSource has already drifted that way, so the
// exhaustive record is the only thing standing between the two copies.
export const EFFORT_LABEL_KEY: Record<AutoPromptEffort, string> = {
	low: "onboarding_auto_prompt.effort.low",
	medium: "onboarding_auto_prompt.effort.medium",
	high: "onboarding_auto_prompt.effort.high",
	xhigh: "onboarding_auto_prompt.effort.xhigh",
	max: "onboarding_auto_prompt.effort.max",
};

// Cheapest/fastest first. Deeper levels catch more ambiguous grid symbols but cost wall-clock, and the
// backend's analysis timeout still bounds the run.
export const EFFORT_ORDER: readonly AutoPromptEffort[] = ["low", "medium", "high", "xhigh", "max"];

// "high" is the model's own default, so it starts here and an untouched run behaves as it always did.
export const DEFAULT_EFFORT: AutoPromptEffort = "high";

// Fixed wizard order: Nurses (roster) -> Shifts -> Template. Also the load order, and it is
// load-bearing: shifts name-match against the roster already in the DB, and a template reconciles its
// occurrences against the loaded shifts (`build_nurse_shift_windows`).
export const KIND_ORDER: readonly AutoPromptFileKind[] = ["roster", "shifts", "template"];

// Convert our "roster" kind to "nurses" (what PreviewCard expects). Shifts and template stay the same.
export const previewFileType = (kind: AutoPromptFileKind): PreviewFileType => (kind === "roster" ? "nurses" : kind);

export const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export const SECTION_HEADERS: Record<keyof AutoPromptEditable, string> = {
	schedule_symbols: "Schedule Symbols",
	locations: "Locations",
	master_schedule_orientation: "Master Schedule Orientation",
	shift_times: "Shift Times",
	name_matching: "Name Matching",
	file_dates: "File Dates",
};

// Editable order matches the design spec.
export const SECTION_ORDER: (keyof AutoPromptEditable)[] = [
	"schedule_symbols",
	"locations",
	"master_schedule_orientation",
	"shift_times",
	"name_matching",
	"file_dates",
];

const errorMessage = (e: unknown): string | undefined => (e as Error)?.message;

function buildEditableFromReport(report: AutoPromptReport): AutoPromptEditable {
	return {
		schedule_symbols: report.schedule_symbols ?? "",
		locations: report.locations ?? "",
		master_schedule_orientation: report.master_schedule_orientation ?? "",
		shift_times: report.shift_times ?? "",
		name_matching: report.name_matching ?? "",
		file_dates: report.file_dates ?? "",
	};
}

// Join the edited sections (and any flags) into the final prompt text, skipping empty sections.
function assembleFinalPrompt(editable: AutoPromptEditable, flags: string[]): string {
	const blocks: string[] = [];
	for (const key of SECTION_ORDER) {
		const value = editable[key].trim();
		if (!value) continue;
		blocks.push(`## ${SECTION_HEADERS[key]}\n${value}`);
	}
	if (flags.length > 0) {
		blocks.push(`## FLAGS / MISSING DATA:\n${flags.map((f) => `- ${f}`).join("\n")}`);
	}
	return blocks.join("\n\n");
}

export function useAutoPromptSection() {
	const { chosenDepartment, setChosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { copyToClipboard } = useCopyToClipboard();
	const { track, trackError } = useAnalytics();

	const [phase, setPhase] = useState<Phase>("upload");
	const [files, setFiles] = useState<AutoPromptFile[]>([]);
	// Survives resetWizard on purpose: it's the onboarder's setting for this session, not wizard state.
	const [effort, setEffort] = useState<AutoPromptEffort>(DEFAULT_EFFORT);
	const [editable, setEditable] = useState<AutoPromptEditable | null>(null);
	const [flags, setFlags] = useState<string[]>([]);
	const [finalPrompt, setFinalPrompt] = useState("");
	// After Transform, holds the result per uploaded FILE (keyed by file id). Missing entry = not transformed.
	const [transformed, setTransformed] = useState<TransformOutcomes>({});
	const [showWeekParityDialog, setShowWeekParityDialog] = useState(false);
	const [isTransforming, setIsTransforming] = useState(false);

	const { mutate: runAutoPrompt, isPending: isAnalyzing } = useRunAutoPrompt();
	const { mutateAsync: transformShiftCsv } = useTransformShiftCsv();
	const { mutateAsync: transformNursesCsv } = useTransformNursesCsv();
	const { mutateAsync: loadNewNurses, isPending: isLoadingNurses } = useLoadNewNurses();
	const { mutateAsync: loadScheduleShifts, isPending: isLoadingShifts } = useLoadScheduleShifts();
	const { mutateAsync: loadRecurringShifts, isPending: isLoadingTemplate } = useLoadScheduleRecurringShifts();
	const { mutateAsync: performUpdateFirstSunday, isPending: isUpdatingFirstSunday } = useUpdateDepartmentFirstSunday();
	const { mutateAsync: performCreatePromptHistory } = useCreatePromptHistory();

	const departmentId = chosenDepartment?.department_id;
	const hasAtLeastOneFile = files.length > 0;
	const hasRanAnalysis = editable !== null;

	// Files grouped by kind, in the order they were added. Every kind can hold several files.
	const filesByKind: Record<AutoPromptFileKind, AutoPromptFile[]> = files.reduce<Record<AutoPromptFileKind, AutoPromptFile[]>>(
		(acc, f) => {
			acc[f.kind].push(f);
			return acc;
		},
		{ roster: [], shifts: [], template: [] },
	);

	// Only shifts files are dated — by the earliest date in their own transformed CSV. Roster and
	// template files split a department by PEOPLE (one export or rotation per unit/wing), so they have
	// no chronology to sort on and keep the order the onboarder listed them in.
	//
	const dateOfFile = (file: AutoPromptFile): DateTime | null => {
		if (file.kind !== "shifts") return null;
		return transformed[file.id]?.earliestDate ?? null;
	};

	const orderedFilesOfKind = (kind: AutoPromptFileKind): AutoPromptFile[] => orderFilesChronologically(filesByKind[kind], dateOfFile);

	// Flat load order across kinds — roster first, then shifts, then template (see KIND_ORDER).
	const orderedFiles: AutoPromptFile[] = KIND_ORDER.flatMap((kind) => orderedFilesOfKind(kind));

	const loadOrderEntries: LoadOrderEntry[] = orderedFiles.map((file) => ({
		id: file.id,
		label: `${KIND_LABEL[file.kind]}: ${file.file.name}`,
		date: dateOfFile(file),
	}));

	const addFiles = (incoming: File[]) => {
		setFiles((prev) => [...prev, ...incoming.map((file) => ({ id: crypto.randomUUID(), file, kind: "roster" as AutoPromptFileKind }))]);
	};

	const handleKindChange = (id: string, kind: AutoPromptFileKind) => {
		setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, kind } : f)));
		// The transform output is shaped by the kind it ran as — a roster CSV rendered as shifts reaches
		// LoadResultDisplay with none of the fields it reads, and would upload to the wrong loader. Drop
		// the outcome so the file is simply un-transformed again.
		setTransformed((prev) => {
			if (!prev[id]) return prev;
			const next = { ...prev };
			delete next[id];
			return next;
		});
	};

	const handleDeleteFile = (id: string) => {
		setFiles((prev) => prev.filter((f) => f.id !== id));
	};

	const resetWizard = () => {
		setFiles([]);
		setEditable(null);
		setFlags([]);
		setFinalPrompt("");
		setTransformed({});
		setShowWeekParityDialog(false);
		setPhase("upload");
	};

	const handleAnalyze = () => {
		if (!departmentId || !hasAtLeastOneFile) return;
		const trackPayload = {
			department_id: departmentId,
			file_count: files.length,
			roster_count: filesByKind.roster.length,
			shifts_count: filesByKind.shifts.length,
			template_count: filesByKind.template.length,
			effort,
		};
		track("auto_prompt_analyze_start", trackPayload);
		runAutoPrompt(
			{
				departmentId,
				effort,
				rosterFiles: filesByKind.roster.map((f) => f.file),
				shiftsFiles: filesByKind.shifts.map((f) => f.file),
				templateFiles: filesByKind.template.map((f) => f.file),
			},
			{
				onSuccess: (report) => {
					setEditable(buildEditableFromReport(report));
					setFlags(report.flags ?? []);
					setPhase("analysis");
					track("auto_prompt_analyze_success", { ...trackPayload, flag_count: report.flags?.length ?? 0 });
				},
				onError: (error) => {
					trackError("auto_prompt_analyze_error", { ...trackPayload, error_message: errorMessage(error) });
				},
			},
		);
	};

	const handleGeneratePrompt = () => {
		if (!editable) return;
		setFinalPrompt(assembleFinalPrompt(editable, flags));
		track("auto_prompt_generate_prompt", { department_id: departmentId });
		setPhase("prompt");
	};

	const handleCopyPrompt = async () => {
		if (!finalPrompt) return;
		const ok = await copyToClipboard(finalPrompt);
		showSnackbar(ok ? "Copied to clipboard" : "Copy failed", ok ? "success" : "error");
	};

	const handleBackToEditing = () => setPhase("analysis");

	const handleTransformAll = async () => {
		if (!departmentId || !finalPrompt) return;
		setIsTransforming(true);
		track("auto_prompt_transform_start", { department_id: departmentId, file_count: files.length });
		try {
			// One transform call per uploaded file, in parallel. Failures yield `null` so the others still render.
			const results = await Promise.all(
				files.map(async (f): Promise<{ id: string; outcome: TransformOutcome } | null> => {
					// Persist the prompt per (department, file_type) so the Transform tab can re-load the last prompt.
					// Best-effort: a save failure is surfaced by the hook's onError but must not block the transform.
					let promptHistoryId: string | undefined;
					try {
						const created = await performCreatePromptHistory({
							departmentId,
							fileType: previewFileType(f.kind),
							prompt: finalPrompt,
							tabInstructions: {},
							file: f.file,
						});
						promptHistoryId = created.id;
					} catch {
						promptHistoryId = undefined;
					}
					try {
						if (f.kind === "roster") {
							const buffer = await transformNursesCsv({ departmentId, file: f.file, instructions: finalPrompt });
							return { id: f.id, outcome: { csvBuffer: buffer, failedTabs: [], skip: false, uploaded: false, promptHistoryId } };
						}
						const { csvBuffer, failedTabs } = await transformShiftCsv({
							departmentId,
							file: f.file,
							isTemplate: f.kind === "template",
							instructions: finalPrompt,
						});
						return {
							id: f.id,
							outcome: { csvBuffer, failedTabs, skip: false, uploaded: false, promptHistoryId, earliestDate: earliestDateInCsv(csvBuffer, SHIFTS_DATE_COLUMN) },
						};
					} catch (error) {
						trackError("auto_prompt_transform_file_error", { department_id: departmentId, kind: f.kind, error_message: errorMessage(error) });
						return null;
					}
				}),
			);
			const next: TransformOutcomes = {};
			for (const r of results) {
				if (r) next[r.id] = r.outcome;
			}
			setTransformed(next);
			setPhase("transform");
			const transformedCount = Object.keys(next).length;
			if (transformedCount === 0) {
				// All files failed - surface as an error, not a success.
				trackError("auto_prompt_transform_error", { department_id: departmentId, file_count: files.length, transformed_count: 0 });
			} else {
				// Mixed success/failure still emits _success with transformed_count so we can tell partial-success runs apart.
				track("auto_prompt_transform_success", { department_id: departmentId, transformed_count: transformedCount });
			}
		} finally {
			setIsTransforming(false);
		}
	};

	const updateOutcome = (fileId: string, partial: Partial<TransformOutcome>) => {
		setTransformed((prev) => {
			const existing = prev[fileId];
			if (!existing) return prev;
			return { ...prev, [fileId]: { ...existing, ...partial } };
		});
	};

	const handleCancelTransform = (fileId: string) => {
		setFiles((prev) => prev.filter((f) => f.id !== fileId));
		setTransformed((prev) => {
			const next = { ...prev };
			delete next[fileId];
			return next;
		});
	};

	const loadFnForKind = (kind: AutoPromptFileKind) => {
		if (kind === "roster") return loadNewNurses;
		if (kind === "shifts") return loadScheduleShifts;
		return loadRecurringShifts;
	};

	// Upload one file. Returns false on failure (caller should stop the chain). No-op for skipped /
	// already-uploaded / not-yet-transformed files.
	const uploadFile = async (file: AutoPromptFile): Promise<boolean> => {
		if (!departmentId) return false;
		const outcome = transformed[file.id];
		if (!outcome || outcome.skip || outcome.uploaded) return true;
		const filenamePrefix = file.kind === "roster" ? "transformed_nurses" : "transformed";
		const blob = outcome.filteredBlob ?? new Blob([outcome.csvBuffer], { type: "text/csv" });
		const csvFile = new File([blob], `${filenamePrefix}_${stripExtension(file.file.name, file.kind)}.csv`, { type: "text/csv" });
		try {
			const result = await loadFnForKind(file.kind)({
				departmentId,
				file: csvFile,
				...(outcome.promptHistoryId ? { promptHistoryId: outcome.promptHistoryId } : {}),
			});
			updateOutcome(file.id, { uploaded: true, loadResult: result as TransformOutcome["loadResult"] });
			track("auto_prompt_upload_success", { department_id: departmentId, kind: file.kind });
			return true;
		} catch (error) {
			trackError("auto_prompt_upload_error", { department_id: departmentId, kind: file.kind, error_message: errorMessage(error) });
			return false;
		}
	};

	const needsWeekParityConfirmation = (file: AutoPromptFile): boolean => {
		const outcome = transformed[file.id];
		return file.kind === "template" && Boolean(outcome) && !outcome?.skip && !outcome?.uploaded && Boolean(chosenDepartment?.first_sunday);
	};

	// Upload the remaining templates. Split out because the WeekParity dialog interrupts the chain and
	// both of its buttons resume from here.
	const uploadRemainingTemplates = async () => {
		for (const file of orderedFilesOfKind("template")) {
			if (!(await uploadFile(file))) return;
		}
	};

	/**
	 * Upload every transformed file, one at a time, in load order. Stops on the first failure; a
	 * template pauses for WeekParity confirmation first.
	 *
	 * Strictly sequential, never Promise.all: each load endpoint reads the department's current state to
	 * de-duplicate (`process_nurses_file` snapshots existing nurses, `insert_shifts` reads the scheduled
	 * shifts in the file's range). Run concurrently, two roster files both see the pre-load state and a
	 * person listed in both is created twice.
	 */
	const handleUploadAll = async () => {
		if (!departmentId) return;
		track("auto_prompt_upload_all_start", { department_id: departmentId, file_count: orderedFiles.length });
		for (const file of orderedFiles) {
			if (needsWeekParityConfirmation(file)) {
				setShowWeekParityDialog(true);
				return;
			}
			if (!(await uploadFile(file))) return;
		}
	};

	const handleSwitchAndUploadTemplate = async () => {
		if (!chosenDepartment?.department_id || !chosenDepartment.first_sunday) return;
		const newFirstSunday = chosenDepartment.first_sunday.plus({ weeks: 1 });
		try {
			await performUpdateFirstSunday({
				departmentId: chosenDepartment.department_id,
				firstSunday: newFirstSunday.toFormat("yyyy-MM-dd"),
			});
			setChosenDepartment((prev) => ({ ...prev, first_sunday: newFirstSunday }));
			setShowWeekParityDialog(false);
			await uploadRemainingTemplates();
		} catch {
			// Error handled by hook's onError -> showErrorSnackbar
		}
	};

	const handleKeepAsIsAndUploadTemplate = async () => {
		setShowWeekParityDialog(false);
		await uploadRemainingTemplates();
	};

	const isUploadAllBusy = isLoadingNurses || isLoadingShifts || isLoadingTemplate;
	// Measured over every file, not over `transformed`: a file whose transform failed has no entry
	// there, so checking only the outcomes would report "All Uploaded" while that file sits unhandled.
	const allRemainingUploaded = files.every((file) => {
		const outcome = transformed[file.id];
		return outcome ? outcome.skip || outcome.uploaded : false;
	});
	// Files the transform produced nothing for. Surfaced so a mixed run says which ones need attention.
	const failedTransformFiles = phase === "transform" ? files.filter((file) => !transformed[file.id]) : [];

	return {
		// Phase + flow state
		phase,
		// Files
		files,
		filesByKind,
		orderedFiles,
		orderedFilesOfKind,
		loadOrderEntries,
		hasAtLeastOneFile,
		addFiles,
		handleKindChange,
		handleDeleteFile,
		// Analysis
		effort,
		setEffort,
		editable,
		setEditable,
		flags,
		isAnalyzing,
		hasRanAnalysis,
		handleAnalyze,
		// Prompt
		finalPrompt,
		setFinalPrompt,
		handleGeneratePrompt,
		handleCopyPrompt,
		handleBackToEditing,
		// Transform
		transformed,
		isTransforming,
		handleTransformAll,
		updateOutcome,
		handleCancelTransform,
		// Upload
		isUploadAllBusy,
		allRemainingUploaded,
		failedTransformFiles,
		handleUploadAll,
		// WeekParity
		chosenDepartment,
		showWeekParityDialog,
		setShowWeekParityDialog,
		handleSwitchAndUploadTemplate,
		handleKeepAsIsAndUploadTemplate,
		isUpdatingFirstSunday,
		// Department gating
		departmentId,
		// Reset
		resetWizard,
	};
}
